import React, { useContext, useEffect, useState } from "react";
import { Form, Button, Table, Col, Row, Alert, Card } from "react-bootstrap";
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../App.css';
import { useNavigate, useLocation } from "react-router-dom";
// import { updateCustomerBalance } from "../../api/api";
import CustomerSelectionModal from "../modals/CustomerSelectionModal";
import { useCustomers } from "../../CustomerContext";
import { useProducts } from "../../ProductContext";
import { useOrders } from "../../OrderContext";
import { CartContext } from "../../CartContext";
// for immediate re-fetch of accounts and transactions after order creation
import { useAccounts } from "../../contextjs/AccountContext";
import { useTransactions } from "../../contextjs/TransactionContext";
// import { sendReceiptToPrinter } from "../../api/api";
import { useUser } from "../../UserContext";
import { fetchStoreAccountSettings } from "../../api/api";

const Checkout = () => {
    const navigate = useNavigate();
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
    const location = useLocation();
    const [amountPaid, setAmountPaid] = useState('');
    const [cashAmount, setCashAmount] = useState(0);
    const [mpesaAmount, setMpesaAmount] = useState(0);
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [showCustomerModal, setShowCustomerModal] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState({}); // state to hold the selected customer [name, phone, balance, balance limit
    const cart = location.state.cart;
    const discount = location.state.discount;

    // accessed through the custom hook useCustomers in the CustomerContext
    const { fetchCustomers, customers } = useCustomers();
    const { fetchAllProducts } = useProducts(); // utilize useProducts to access fetchAllProducts
    // utilize useOrder to access createOrder when form is submitted
    const { createOrder } = useOrders();
    const { clearCart } = useContext(CartContext);
    // for immediate re-fetch of accounts and transactions after order creation
    const { fetchAccountsData } = useAccounts();
    const { loadTransactions } = useTransactions();

    const { user } = useUser();
    const [orderNotes, setOrderNotes] = useState('');
    const [paymentSettings, setPaymentSettings] = useState([]);
    // const createdAt = new Date().toLocaleString('en-GB', { timeZone: 'Africa/Nairobi' });
    const createdAt = new Date().toISOString(); // international format needs to be parsed to local time

    useEffect(() => {
        fetchCustomers();
    }, [fetchCustomers]);

    useEffect(() => {
        fetchStoreAccountSettings({ usage_type: "PAYMENT" })
            .then((settings) => setPaymentSettings(settings.filter((setting) => setting.is_enabled)))
            .catch((loadError) => {
                console.error("Unable to load store payment accounts", loadError);
                setError("Unable to load payment accounts for this store.");
            });
    }, []);

    const getPaymentSetting = (channel) => {
        const matches = paymentSettings.filter((setting) => setting.payment_channel === channel);
        return matches.find((setting) => setting.is_default) || matches[0] || null;
    };
    const cashSetting = getPaymentSetting("CASH");
    const tillSetting = getPaymentSetting("TILL");
    const hasConfiguredPaymentRules = paymentSettings.length > 0;
    const cashAvailable = !hasConfiguredPaymentRules || Boolean(cashSetting);
    const tillAvailable = !hasConfiguredPaymentRules || Boolean(tillSetting);

    // VAT rate
    const VAT_RATE = 0.16;
    const getLineSubtotal = (item) => Number(item.line_subtotal ?? (item.price * item.quantity)) || 0;
    const getLineDiscount = (item) => Number(item.quantity_discount ?? 0) || 0;
    const getDiscountedLineSubtotal = (item) => Math.max(0, getLineSubtotal(item) - getLineDiscount(item));
    const getQuantityDiscountTotal = () => cart.reduce((total, item) => total + getLineDiscount(item), 0);

    // calculate pre-tax subtotal
    const calculatePreTaxSubtotal = () => {
        return cart.reduce((total, item) => {
            const itemTotal = getDiscountedLineSubtotal(item);
            if (item.is_vat_applicable) {
                const preTaxPrice = itemTotal / (1 + VAT_RATE);
                return total + preTaxPrice;
            } else {
                return total + itemTotal;
            }
        }, 0);
    };

    // calculate VAT amount function
    const calculateVatAmount = () => {
        return cart.reduce((total, item) => {
            if (item.is_vat_applicable) {
                const itemTotal = getDiscountedLineSubtotal(item);
                const preTaxPrice = itemTotal / (1 + VAT_RATE);
                const vatAmount = itemTotal - preTaxPrice;
                return total + vatAmount;
            }
            return total;
        }, 0);
    };

    const calculateSubtotal = () => {
        return Math.ceil(cart.reduce((acc, item) => acc + getDiscountedLineSubtotal(item), 0));
    };

    const calculateGrossSubtotal = () => {
        return Math.ceil(cart.reduce((acc, item) => acc + getLineSubtotal(item), 0));
    };

    const calculateTotal = () => {
        return calculateSubtotal() - discount;
    };

    const total = calculateTotal();

    const handlePaymentMethodChange = (method) => {
        setSelectedPaymentMethod(method);
        setError(''); // reset error message when the payment method changes
        //reset the amount paid fields only if it's not customerAccount
        if (method !== 'customerAccount') {
            setCashAmount(0);
            setMpesaAmount(0);
            setAmountPaid('');
        }
    };

    const handleAmountPaidChange = (e) => {
        setAmountPaid(e.target.value);
        setError(''); // reset error message when the amount paid changes
    };

    const calculateAmountPaid = () => {
        if (selectedPaymentMethod === 'cashAndMpesa') {
            return (parseFloat(cashAmount || '0')) + (parseFloat(mpesaAmount || '0'));
        } else if (selectedPaymentMethod === 'cash' || selectedPaymentMethod === 'mpesa') {
            return parseFloat(amountPaid || '0'); // use fallback value of 0 if amountPaid is not a number
        } else if (selectedPaymentMethod === 'customerAccount')
        return 0;
    };

    const currentAmountPaid = calculateAmountPaid();

    // calculate the change to be given back to the customer
    const changeToGive = currentAmountPaid - total > 0 ? currentAmountPaid - total : 0;

    // for showing amount remaining in cashAndMpesa payment method
    const calculateAmountRemaining = () => {
        const paid = (parseFloat(cashAmount || '0')) + (parseFloat(mpesaAmount || '0'));
        return Math.max(total - paid, 0);
    };

    // for displaying customer selected
    const handleCustomerSelection = (customer) => {
        const bal = Number(customer.customer_balance);
        const lim = Number(customer.customer_balance_limit);
        setSelectedCustomer({
            id: customer.id, name: customer.customer_name, phone: customer.phone_number,
            balance: Number.isFinite(bal) ? bal : 0, balanceLimit: Number.isFinite(lim) ? lim : 0,
        });
        setShowCustomerModal(false);
    };

    const getOrderErrorMessage = (error) => {
        const responseData = error?.response?.data;

        if (Array.isArray(responseData?.items) && responseData.items.length > 0) {
            return responseData.items[0];
        }

        if (typeof responseData?.detail === 'string' && responseData.detail.trim()) {
            return responseData.detail;
        }

        if (typeof responseData === 'string' && responseData.trim()) {
            return responseData;
        }
        return 'Error processing order';
    };

    const handleSubmitOrder = async (e) => {
        e.preventDefault();

        if (submitting) return; // prevent multiple submissions
        setSubmitting(true);

        // check if a payment method is selected
        if (!selectedPaymentMethod) {
            setError('Please select a payment method');
            setSubmitting(false);
            return; // exit the function if validation fails
        }

        const currentAmountPaid = calculateAmountPaid();
        setError(''); // reset the error message when the form is submitted

        // Check if the amount paid is sufficient
        if ((selectedPaymentMethod === 'cash' || selectedPaymentMethod === 'mpesa' || selectedPaymentMethod === 'cashAndMpesa')
            && currentAmountPaid < total) {
            setError('Amount paid cannot be less than the total amount');
            setSubmitting(false);
            return; // exit the function if validation fails
        }

        // handling the case where both cash and mpesa (cashAndMpesa) are selected
        let cashAmountValue = parseFloat(cashAmount || '0');
        let mpesaAmountValue = parseFloat(mpesaAmount || '0');

        if (selectedPaymentMethod === 'cashAndMpesa') {
            const change = (cashAmountValue + mpesaAmountValue) - total;

            if (change > 0) {
                // deduct change from cash amount if there is excess payment
                cashAmountValue -= change;
            }
        }

        // Create the order data
        let orderData = {
            items: cart.map(item => ({
                // product_id: item.id, //this is cart item id
                product_id: item.product, // product id
                quantity: item.quantity,
                unit_price: item.price,
                conversion_factor: item.conversion_factor || 1.0,
                // for profit calculation
                unit_cost_price: item.unit_cost_price || 0,
                unit_name: item.unit_name || '',
                unit_id: item.unit_id || null,
            })),
            total_amount: calculateGrossSubtotal(),
            discount: discount || 0,
            vatAmount: calculateVatAmount(),
            final_amount: calculateSubtotal() - (discount || 0),
            payment_method: selectedPaymentMethod,
            amount_paid: currentAmountPaid,
            // to ensure both cash_amount and mpesa_amount are included in the order data for sales transactions
            cash_amount: selectedPaymentMethod === 'cashAndMpesa' ? cashAmountValue : undefined,
            mpesa_amount: selectedPaymentMethod === 'cashAndMpesa' ? mpesaAmountValue : undefined,
            customer: selectedCustomer ? selectedCustomer.id : null,
            order_notes: orderNotes || '',
            audit_log: []
        };
        if (cashSetting && ['cash', 'cashAndMpesa'].includes(selectedPaymentMethod)) {
            orderData.cash_account = cashSetting.account;
        }
        if (tillSetting && ['mpesa', 'cashAndMpesa'].includes(selectedPaymentMethod)) {
            orderData.till_account = tillSetting.account;
        }

        try {
            // console.log(' payload sent to /orders/:', JSON.stringify(orderData, null, 2));
            const createdOrderResponse = await createOrder(orderData);
            // added
            // prevent receipt printing & going to receipt page if order creation failed
            if (
                !createdOrderResponse ||
                !createdOrderResponse.order_number
            ) {
                setError('Order was not created. Please try again.');
                return; // Stop further execution if order creation failed
            }
            // end of added

            const { order_number } = createdOrderResponse || {};

            // Prepare receipt data
            const receiptData = {
                order_number,
                cart: cart.map(item => ({
                    name: item.name,
                    quantity: item.quantity,
                    price: item.price,
                    unit_id: item.unit_id,
                    unit_name: item.unit_name,
                    quantity_discount: getLineDiscount(item),
                    quantity_discount_reason: item.quantity_discount_reason || '',
                    discounted_subtotal: getDiscountedLineSubtotal(item),
                })),
                served_by: user.username,
                created_at: createdAt,
                total: Math.ceil(total),
                discount: discount || 0,
                vatAmount: calculateVatAmount(),
                preTaxSubtotal: calculatePreTaxSubtotal(),
                payment_method: selectedPaymentMethod,
                amountPaid: currentAmountPaid,
                change: changeToGive.toFixed(2),
                customer: selectedCustomer.name || "Walk-in",
                order_notes: orderNotes || '',
            };

            // Navigate to the receipt page if the order is successfully created
            navigate('/pos/receipt', {
                state: {
                    cart,
                    total,
                    discount,
                    paymentMethod: selectedPaymentMethod,
                    amountPaid: currentAmountPaid,
                    changeToGive,
                    selectedCustomer: selectedCustomer,
                    vatAmount: calculateVatAmount(),
                    preTaxSubtotal: calculatePreTaxSubtotal(),
                    created_at: createdAt,
                }
            });

            // Follow-up work should not hold the cashier on checkout.
            fetch("/api/print-receipt/", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(receiptData),
            })
                .then((printResponse) => {
                    if (!printResponse.ok) {
                        console.warn("Receipt print request failed:", printResponse.status);
                    }
                })
                .catch((printError) => console.warn("Receipt print request failed:", printError));

            clearCart().catch((cartError) => console.warn("Cart clear failed after checkout:", cartError));
            fetchCustomers().catch((customerError) => console.warn("Customer refresh failed after checkout:", customerError));
            fetchAllProducts().catch((productError) => console.warn("Product refresh failed after checkout:", productError));
            fetchAccountsData().catch((accountError) => console.warn("Account refresh failed after checkout:", accountError));
            loadTransactions().catch((transactionError) => console.warn("Transaction refresh failed after checkout:", transactionError));

            console.log("Order processed; receipt and refresh work queued.");

        } catch (error) {
            console.error('Error processing order:', error);
            setError(getOrderErrorMessage(error));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="checkout-page">
            <div className="back-button-container">
                <Button variant="secondary" onClick={() => navigate(-1)}>Back</Button>
            </div>
            <h2>Checkout</h2>
            <Table striped bordered hover>
                <thead>
                    <tr>
                        <th>Product</th>
                        <th>Quantity</th>
                        <th>Unit Price</th>
                        <th>Subtotal</th>
                    </tr>
                </thead>
                <tbody>
                    {cart.map((item, index) => (
                        <tr key={index}>
                            <td>{item.name}
                                {/*{item.isAlternativeUnit ? ` ${item.unit_name || item.unit_abbreviation || 'Unit'}` : ''}*/}
                                {/*{item.unit_name ? ` ${item.unit_name}` : ""}*/}
                                {item.unit_id && item.unit_name ? ` ${item.unit_name}` : ""}
                            </td>
                            <td>{item.quantity}</td>
                            <td>{parseFloat(item.price).toFixed(2)}</td>
                            <td>
                                <div>{getDiscountedLineSubtotal(item).toFixed(2)}</div>
                                {getLineDiscount(item) > 0 && (
                                    <div className="text-success small">
                                        {item.quantity_discount_reason || `Quantity discount: -Ksh. ${getLineDiscount(item).toFixed(2)}`}
                                    </div>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
            <p>Subtotal (pre VAT): Ksh.{calculatePreTaxSubtotal().toFixed(2)}</p>
            <p>VAT: Ksh.{calculateVatAmount().toFixed(2)}</p>
            {getQuantityDiscountTotal() > 0 && (
                <p className="text-success">Quantity Discount: -Ksh.{getQuantityDiscountTotal().toFixed(2)}</p>
            )}
            <p>Discount: Ksh.{discount.toFixed(2)}</p>
            {/*<h4>Total: Ksh.{total.toFixed(2)}</h4>*/}
            <h4>Total: Ksh.{Math.ceil(total)}</h4>

            <Row className="justify-content-md-center">
                <Col md={8}>
                    <Form onSubmit={handleSubmitOrder}>

                        {/* Payment Method Cards */}
                        <Row className="mb-3">
                            {cashAvailable && <Col md={3}>
                                <Card
                                    className="payment-method-card"
                                    onClick={() => handlePaymentMethodChange('cash')}
                                    style={{ cursor: 'pointer', backgroundColor: selectedPaymentMethod === 'cash' ? '#f8d7da' : 'orangered' }}
                                >
                                    <Card.Body>
                                        <Card.Title>{cashSetting?.label_override || cashSetting?.account_name || "Cash"}</Card.Title>
                                    </Card.Body>
                                </Card>
                            </Col>}
                            {tillAvailable && <Col md={3}>
                                <Card
                                    className="payment-method-card"
                                    onClick={() => handlePaymentMethodChange('mpesa')}
                                    style={{ cursor: 'pointer', backgroundColor: selectedPaymentMethod === 'mpesa' ? '#d1ecf1' : 'darkgray' }}
                                >
                                    <Card.Body>
                                        <Card.Title>{tillSetting?.label_override || tillSetting?.account_name || "Mpesa"}</Card.Title>
                                    </Card.Body>
                                </Card>
                            </Col>}
                            {cashAvailable && tillAvailable && <Col md={3}>
                                <Card
                                    className="payment-method-card"
                                    onClick={() => handlePaymentMethodChange('cashAndMpesa')}
                                    style={{ cursor: 'pointer', backgroundColor: selectedPaymentMethod === 'cashAndMpesa' ? '#d4edda' : 'skyblue' }}
                                >
                                    <Card.Body>
                                        <Card.Title>Cash and {tillSetting?.label_override || "Till"}</Card.Title>
                                    </Card.Body>
                                </Card>
                            </Col>}
                            <Col md={3}>
                                <Card
                                    className="payment-method-card"
                                    onClick={() => handlePaymentMethodChange('customerAccount')}
                                    style={{ cursor: 'pointer', backgroundColor: selectedPaymentMethod === 'customerAccount' ? '#fff3cd' : 'lawngreen' }}
                                >
                                    <Card.Body>
                                        <Card.Title>Customer Account</Card.Title>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Single or both payment method card */}
                        <>
                            {selectedPaymentMethod && selectedPaymentMethod !== 'customerAccount' &&(
                                <Card className="mb-3" style={{backgroundColor: '#f8d7da'}}>
                                    <Card.Body>
                                        <Form.Group as="div">
                                            <Form.Label>Amount Paid</Form.Label>
                                            <Form.Control
                                                type="number"
                                                value={selectedPaymentMethod === 'cashAndMpesa' ? calculateAmountPaid() : amountPaid}
                                                onChange={handleAmountPaidChange}
                                                disabled={selectedPaymentMethod === 'cashAndMpesa'} //disable if both payment method is selected
                                            />
                                        </Form.Group>
                                        <div className="mt-2">
                                            <strong>Change to give: </strong>
                                            <span>{changeToGive.toFixed(2)}</span>
                                        </div>
                                    </Card.Body>
                                </Card>
                            )}

                            {/* Both cash and mpesa Payment Card */}
                            {selectedPaymentMethod === 'cashAndMpesa' && (
                                <>
                                    <Card className="mb-3">
                                        <Card.Body>
                                            <Form.Group controlId="formCashAmount">
                                                <Form.Label>Cash Amount</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    value={cashAmount}
                                                    onChange={(e) => setCashAmount(parseFloat(e.target.value || '0'))}
                                                    placeholder="Enter cash amount"
                                                />
                                            </Form.Group>
                                            <Form.Group controlId="formMpesaAmount">
                                                <Form.Label>Mpesa Amount</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    value={mpesaAmount}
                                                    onChange={(e) => setMpesaAmount(parseFloat(e.target.value || '0'))}
                                                    placeholder="Enter mpesa amount"
                                                />
                                            </Form.Group>
                                        </Card.Body>
                                    </Card>
                                    <div className="mt-2">
                                        <strong>Remaining to pay: </strong>{' '}
                                        <span className={
                                            calculateAmountRemaining() === 0 ? 'text-success' : 'text-danger'}
                                        >
                                           Ksh. {calculateAmountRemaining().toFixed(2)}
                                        </span>
                                    </div>
                                </>
                            )}

                            {/* Customer Account Card */}
                            {selectedPaymentMethod === 'customerAccount' && (
                                <div>
                                    <Button variant="primary" onClick={() => setShowCustomerModal(true)}>Select Customer</Button>
                                    <CustomerSelectionModal
                                        show={showCustomerModal}
                                        handleClose={() => setShowCustomerModal(false)}
                                        customers={customers}
                                        handleCustomerSelection={handleCustomerSelection}
                                    />
                                    {selectedCustomer && (
                                        <Card className="mt-3">
                                            <Card.Body>
                                                <p>Selected Customer: {selectedCustomer.name}</p>
                                                <p>Phone: {selectedCustomer.phone}</p>
                                                <p>Current Balance:
                                                    Ksh. {(Number(selectedCustomer?.balance ?? 0)).toFixed(2)}</p>
                                                <p>New balance after order:
                                                    Ksh. {(Number(selectedCustomer?.balance ?? 0) + total).toFixed(2)}</p>
                                            </Card.Body>
                                        </Card>
                                    )}
                                </div>
                            )}
                        </>

                        <Form.Group className="mt-3">
                            <Form.Label>Order Notes (optional)</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={orderNotes}
                                onChange={(e) => setOrderNotes(e.target.value)}
                                placeholder="Add any notes for this order..."
                            />
                        </Form.Group>

                        {error && <Alert variant="danger">{error}</Alert>}

                        <div className="text-center">
                            <br />
                            <Button variant="success" type="submit" disabled={submitting}>
                                {submitting ? 'Processing Order...' : 'Submit Order'}
                            </Button>
                        </div>
                    </Form>
                </Col>
            </Row>
        </div>
    );
};

export default Checkout;
